import { createInertiaApp } from "@inertiajs/react";
import type { PageProps } from "@inertiajs/core";
import type { SetupOptions } from "@inertiajs/react/types/createInertiaApp";
import { createRoot } from "react-dom/client";

import { createElement } from "react";
import { Toaster } from "@/components/ui/toaster";
import {
  BookmarkToast,
  BookmarkLoginBanner,
} from "@/components/ui/bookmark-button";
import { DevelopmentErrorBoundary } from "@/shared/error/development-error-boundary";
import { TypeScriptErrorOverlay } from "@/shared/error/typescript-error-overlay";
import { developmentErrorLogger } from "@/utils/development-error-logger";
import { ErrorBoundary } from "@/shared/error/error-boundary";
import { MissingTemplate } from "@/shared/error/missing-template";
import { FlashToastBridge } from "@/shared/flash-toast-bridge";
import { initGtm, type InitialPage } from "@/lib/gtm";

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: any;
  layout?: (page: any) => any;
};

const wrappedPages = new WeakSet<object>();

// Global layout wrapper
const DefaultLayout = ({ children }: { children: React.ReactNode }) => {
  const isDevelopment =
    process.env.NODE_ENV === "development" ||
    window.location.hostname === "localhost" ||
    window.location.hostname.includes(".lvh.me");

  const content = (
    <>
      {children}
      <FlashToastBridge />
      <BookmarkToast />
      <BookmarkLoginBanner />
      <Toaster />
      {isDevelopment && <TypeScriptErrorOverlay />}
    </>
  );

  const pageContent = (
    <div className="min-h-screen" style={{ backgroundColor: "#f3f4f6" }}>
      {content}
    </div>
  );

  // Wrap with appropriate error boundary
  return isDevelopment ? (
    <DevelopmentErrorBoundary>{pageContent}</DevelopmentErrorBoundary>
  ) : (
    <ErrorBoundary>{pageContent}</ErrorBoundary>
  );
};

// createInertiaApp がクラッシュしたとき（eager glob のモジュール評価エラーなど）に
// 真っ白にならないよう DOM に直接エラー画面を描画する
function renderFatalError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? (error.stack ?? "") : "";
  const url = window.location.href;
  const timestamp = new Date().toISOString();

  // LLM に貼り付けやすいマークダウン形式
  const markdownReport = [
    "## Fatal Error Report",
    "",
    `- **URL**: ${url}`,
    `- **Timestamp**: ${timestamp}`,
    `- **Message**: ${message}`,
    "",
    "### Stack Trace",
    "```",
    stack,
    "```",
  ].join("\n");

  const el = document.getElementById("app") ?? document.body;
  el.innerHTML = `
    <div style="
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fafafa;
      font-family: ui-monospace, monospace;
      padding: 2rem;
    ">
      <div style="
        max-width: 640px;
        width: 100%;
        background: #fff;
        border: 1px solid #f87171;
        border-radius: 12px;
        padding: 2rem;
        box-shadow: 0 4px 24px rgba(0,0,0,0.08);
      ">
        <p style="font-size: 11px; font-weight: 700; letter-spacing: 0.1em; color: #ef4444; text-transform: uppercase; margin: 0 0 0.5rem;">
          Fatal Error — Page failed to load
        </p>
        <h1 style="font-size: 18px; font-weight: 700; color: #111; margin: 0 0 1rem; font-family: ui-sans-serif, sans-serif;">
          ${message.replace(/</g, "&lt;")}
        </h1>
        <pre style="
          font-size: 11px;
          line-height: 1.6;
          color: #555;
          background: #f5f5f5;
          border-radius: 6px;
          padding: 1rem;
          overflow-x: auto;
          white-space: pre-wrap;
          word-break: break-all;
          margin: 0;
        ">${stack.replace(/</g, "&lt;")}</pre>
        <div style="display: flex; gap: 0.75rem; margin-top: 1.5rem; flex-wrap: wrap;">
          <button
            onclick="location.reload()"
            style="
              padding: 0.5rem 1.25rem;
              background: #111;
              color: #fff;
              border: none;
              border-radius: 999px;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
            "
          >
            ページを再読み込み
          </button>
          <button
            id="copy-error-btn"
            style="
              padding: 0.5rem 1.25rem;
              background: #fff;
              color: #111;
              border: 1px solid #d1d5db;
              border-radius: 999px;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
            "
          >
            📋 LLM 用にコピー
          </button>
        </div>
      </div>
    </div>
  `;

  // コピーボタンのイベントを innerHTML 描画後に設定
  document.getElementById("copy-error-btn")?.addEventListener("click", (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    navigator.clipboard
      .writeText(markdownReport)
      .then(() => {
        btn.textContent = "✅ コピーしました";
        setTimeout(() => {
          btn.textContent = "📋 LLM 用にコピー";
        }, 2000);
      })
      .catch(() => {
        btn.textContent = "❌ コピー失敗";
      });
  });
}

createInertiaApp({
  resolve: async (name: string) => {
    const pages = import.meta.glob<ResolvedComponent>("./_pages/**/[!_]*.tsx");
    const loader = pages[`./_pages/${name}.tsx`];

    if (!loader) {
      const availablePages = Object.keys(pages).map((p) =>
        p.replace("./_pages/", "").replace(".tsx", ""),
      );

      const error = new Error(`Missing Inertia page component: '${name}.tsx'`);
      console.error(error);
      developmentErrorLogger.logError(error, {
        component: name,
        availablePages,
      });

      const MissingTemplateComponent = () =>
        createElement(MissingTemplate, {
          templateName: name,
          availableTemplates: availablePages,
        });

      MissingTemplateComponent.layout = (page: React.ReactNode) => {
        return createElement(DefaultLayout, null, page);
      };

      return { default: MissingTemplateComponent };
    }

    const page = await loader();

    if (!page.default) {
      const availablePages = Object.keys(pages).map((p) =>
        p.replace("./_pages/", "").replace(".tsx", ""),
      );

      const error = new Error(
        `Page component '${name}.tsx' has no default export`,
      );
      console.error(error);
      developmentErrorLogger.logError(error, {
        component: name,
        availablePages,
      });

      const MissingDefaultComponent = () =>
        createElement(MissingTemplate, {
          templateName: name,
          availableTemplates: availablePages,
        });

      MissingDefaultComponent.layout = (page: React.ReactNode) => {
        return createElement(DefaultLayout, null, page);
      };

      return { default: MissingDefaultComponent };
    }

    if (!wrappedPages.has(page.default)) {
      const originalLayout = page.default.layout;
      page.default.layout = (page: React.ReactNode) => {
        if (originalLayout) {
          return createElement(DefaultLayout, null, originalLayout(page));
        }
        return createElement(DefaultLayout, null, page);
      };
      wrappedPages.add(page.default);
    }

    return page;
  },

  setup({ el, App, props }: SetupOptions<HTMLElement, PageProps>) {
    initGtm(props.initialPage as InitialPage);
    createRoot(el).render(<App {...props} />);
  },
}).catch(renderFatalError);
